import { Component, OnInit, ViewChild, Input, Output, EventEmitter, AfterViewInit, OnDestroy, OnChanges, SimpleChanges, inject, ChangeDetectionStrategy, signal } from '@angular/core';

import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FeatureService } from '../../shared/services/feature/feature.service';
import { ProjectService } from '../../shared/services/project/project.service';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, catchError, of, Subject, takeUntil } from 'rxjs';

// Combined feature interface for display
export interface FeatureDisplayItem {
  featureId: number;
  name: string;
  displayName: string;
  enabled: boolean;
}

/**
 * FeaturesComponent - A component for managing project features
 *
 * This component displays all available features in the system and allows
 * enabling/disabling them for a specific project. It integrates with the
 * FeatureService and ProjectService to provide real-time feature management.
 *
 * @example
 * ```html
 * <app-features
 *   [projectId]="selectedProjectId"
 *   (featureToggled)="onFeatureToggled($event)"
 *   (featuresLoaded)="onFeaturesLoaded($event)">
 * </app-features>
 * ```
 *
 * @example
 * ```typescript
 * export class MyComponent {
 *   selectedProjectId = 1001;
 *
 *   onFeatureToggled(feature: FeatureDisplayItem) {
 *     console.log(`Feature ${feature.displayName} is now ${feature.enabled ? 'enabled' : 'disabled'}`);
 *   }
 *
 *   onFeaturesLoaded(features: FeatureDisplayItem[]) {
 *     console.log('Loaded features:', features);
 *   }
 * }
 * ```
 */
@Component({
  selector: 'app-features',
  standalone: true,
  imports: [
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatSortModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  templateUrl: './features.component.html',
  styleUrls: ['./features.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeaturesComponent
  implements OnInit, AfterViewInit, OnDestroy, OnChanges
{
  private featureService = inject(FeatureService);
  private projectService = inject(ProjectService);
  private toastr = inject(ToastrService);

  /** The ID of the project to manage features for */
  @Input() projectId: number | null = null;

  /** Emitted when a feature is toggled (enabled/disabled) */
  @Output() featureToggled = new EventEmitter<FeatureDisplayItem>();

  /** Emitted when features are successfully loaded */
  @Output() featuresLoaded = new EventEmitter<FeatureDisplayItem[]>();

  dataSource = new MatTableDataSource<FeatureDisplayItem>([]);
  displayedColumns: string[] = ['featureName', 'enabled'];
  loading = signal(false);
  error = signal<string | null>(null);

  @ViewChild(MatSort) sort!: MatSort;
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    if (this.projectId) {
      this.loadFeatures();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId']) {
      const currentValue = changes['projectId'].currentValue;
      if (currentValue) {
        this.projectId = currentValue;
        this.error.set(null);
        this.loadFeatures();
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.sort) {
      if (!(this.sort as any).sortChange) {
        (this.sort as any).sortChange = of();
      }
      try {
        this.dataSource.sort = this.sort;
      } catch {
        (this.dataSource as any)._sort = this.sort;
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load features for the current project
   * Combines all available features with the project's enabled features
   */
  private loadFeatures(): void {
    if (!this.projectId || this.projectId <= 0) {
      this.error.set('Invalid project ID provided');
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    // Load both all features and current project to get enabled features
    forkJoin({
      features: this.featureService.getFeatures(),
      project: this.projectService.getProject(this.projectId),
    })
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error('Error loading features:', error);
          this.error.set('Failed to load features. Please try again.');

          // Try to determine the specific error
          if (error.status === 404) {
            this.error.set('Project not found. Please verify the project exists.');
          } else if (error.status === 403) {
            this.error.set(
              'You do not have permission to view features for this project.',
            );
          } else if (error.status === 500) {
            this.error.set('Server error occurred while loading features.');
          }

          this.toastr.error(this.error()!);
          return of({ features: [], project: null });
        }),
      )
      .subscribe(({ features, project }) => {
        this.loading.set(false);

        if (this.error()) {
          return; // Don't process data if there was an error
        }

        const enabledFeatureIds = project?.featureIds || [];

        // Combine features with their enabled status
        const featureDisplayItems: FeatureDisplayItem[] = features.map(
          (feature) => ({
            featureId: feature.featureId,
            name: feature.name,
            displayName: feature.displayName,
            enabled: enabledFeatureIds.includes(feature.featureId),
          }),
        );

        this.dataSource.data = featureDisplayItems;
        this.featuresLoaded.emit(featureDisplayItems);
      });
  }

  /**
   * Toggle a feature for the current project
   * @param feature The feature to toggle
   */
  onToggleFeature(feature: FeatureDisplayItem): void {
    if (!this.projectId || this.projectId <= 0) {
      this.toastr.error('Invalid project ID');
      return;
    }

    if (this.loading()) {
      return; // Prevent multiple simultaneous requests
    }

    const wasEnabled = feature.enabled;
    const action = wasEnabled ? 'disable' : 'enable';

    // Optimistically update the UI
    feature.enabled = !wasEnabled;
    this.loading.set(true);

    const operation = wasEnabled
      ? this.projectService.removeProjectIntegration(
          this.projectId,
          feature.featureId,
        )
      : this.projectService.addProjectIntegration(
          this.projectId,
          feature.featureId,
        );

    operation
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error(`Error ${action}ing feature:`, error);

          // Revert the optimistic update
          feature.enabled = wasEnabled;

          let errorMessage = `Failed to ${action} feature: ${feature.displayName}`;
          if (error.status === 403) {
            errorMessage = `You do not have permission to ${action} this feature`;
          } else if (error.status === 404) {
            errorMessage = 'Feature or project not found';
          }

          this.toastr.error(errorMessage);
          return of(null);
        }),
      )
      .subscribe((updatedProject) => {
        this.loading.set(false);

        if (updatedProject !== null) {
          this.featureToggled.emit(feature);
          this.toastr.success(
            `Successfully ${wasEnabled ? 'disabled' : 'enabled'} ${feature.displayName}`,
          );
        }
      });
  }

  /**
   * Refresh the features list
   * Call this method to reload features from the server
   */
  refresh(): void {
    if (this.projectId) {
      this.loadFeatures();
    }
  }

  /**
   * Handle Add New button click
   * Features are typically managed at the system level, so this shows an info message
   */
  onAddNew(): void {
    this.toastr.info(
      'Features are managed at the system level. Contact your administrator to add new features to the system.',
    );
  }
}
